import {
    Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, Output, ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { ReportProblemService } from 'src/app/global/services/report-problem/report-problem.abstract.service';
import { ReportAttachment } from '../report-problem.model';

const ACCEPTED_NAME_PATTERN = /\.(jpe?g|png|gif|webp|bmp|svg|heic|avif|pdf)$/i;

interface AttachmentItem {
    file: File;
    isPdf: boolean;
    previewUrl?: SafeUrl;
    objectUrl?: string;
    uploading: boolean;
    attachment?: ReportAttachment;
}

export type AttachmentRejectionReason = 'type' | 'size' | 'count' | 'upload';

export interface AttachmentRejection {
    name: string;
    reason: AttachmentRejectionReason;
}

function pad(value: number): string {
    return String(value).padStart(2, '0');
}

/** Pasted screenshots are all called image.png: give them a readable, unique name. */
export function toPastedFileName(date: Date, extension = 'png'): string {
    const day = `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}`;
    const time = `${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
    return `capture-${day}-${time}.${extension}`;
}

/**
 * Attachment tiles for the problem report, following the request-v2 detail files pattern:
 * files are uploaded as soon as they are added, so sending the report only posts URLs.
 */
@Component({
    selector: 'app-report-attachments',
    templateUrl: './report-attachments.component.html',
    styleUrls: ['./report-attachments.component.scss'],
})
export class ReportAttachmentsComponent implements OnDestroy {
    @Input() maxFiles = 5;

    @Input() maxFileSizeMB = 10;

    @Input() disabled = false;

    @Output() attachmentsChanged = new EventEmitter<ReportAttachment[]>();

    @Output() uploadingChange = new EventEmitter<boolean>();

    @Output() dragOverChange = new EventEmitter<boolean>();

    @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

    items: AttachmentItem[] = [];

    rejections: AttachmentRejection[] = [];

    isDragOver = false;

    private dragCounter = 0;

    private destroy$ = new Subject<void>();

    constructor(
        private reportProblemService: ReportProblemService,
        private sanitizer: DomSanitizer,
    ) {}

    get uploading(): boolean {
        return this.items.some((item) => item.uploading);
    }

    @HostListener('document:dragenter', ['$event'])
    onDocumentDragEnter(event: DragEvent): void {
        if (event.dataTransfer?.types?.includes('Files')) {
            this.dragCounter += 1;
            this.setDragOver(true);
        }
    }

    @HostListener('document:dragleave')
    onDocumentDragLeave(): void {
        this.dragCounter = Math.max(0, this.dragCounter - 1);
        if (this.dragCounter === 0) this.setDragOver(false);
    }

    @HostListener('document:dragover', ['$event'])
    onDocumentDragOver(event: DragEvent): void {
        event.preventDefault();
    }

    @HostListener('document:drop', ['$event'])
    onDocumentDrop(event: DragEvent): void {
        event.preventDefault();
        this.dragCounter = 0;
        this.setDragOver(false);
    }

    /** Called by the dialog so a drop anywhere on the card is accepted. */
    onDrop(event: DragEvent): void {
        const files = Array.from(event.dataTransfer?.files || []);
        if (files.length) this.addFiles(files);
    }

    /** Returns true when the clipboard held images, so text paste in the textarea is left alone. */
    handlePaste(event: ClipboardEvent): boolean {
        const images = Array.from(event.clipboardData?.items || [])
            .filter((item) => item.kind === 'file' && item.type.startsWith('image/'))
            .map((item) => item.getAsFile())
            .filter((file): file is File => !!file);

        if (!images.length) {
            return false;
        }

        event.preventDefault();
        const now = new Date();
        this.addFiles(
            images.map((image, index) => {
                const extension = image.type.split('/')[1] || 'png';
                const name = toPastedFileName(new Date(now.getTime() + index * 1000), extension);
                return new File([image], name, { type: image.type });
            }),
        );
        return true;
    }

    openFilePicker(): void {
        if (this.disabled) return;
        this.fileInput.nativeElement.click();
    }

    onFileSelected(event: Event): void {
        const target = event.target as HTMLInputElement;
        const files = Array.from(target.files || []);
        target.value = '';
        if (files.length) this.addFiles(files);
    }

    addFiles(files: File[]): void {
        if (this.disabled) return;

        this.rejections = [];
        const accepted: File[] = [];
        files.forEach((file) => {
            if (!this.isAcceptedType(file)) {
                this.rejections.push({ name: file.name, reason: 'type' });
            } else if (file.size > this.maxFileSizeMB * 1024 * 1024) {
                this.rejections.push({ name: file.name, reason: 'size' });
            } else if (this.items.length + accepted.length >= this.maxFiles) {
                if (!this.rejections.some((rejection) => rejection.reason === 'count')) {
                    this.rejections.push({ name: file.name, reason: 'count' });
                }
            } else {
                accepted.push(file);
            }
        });

        if (!accepted.length) return;

        const newItems = accepted.map((file) => this.toItem(file));
        this.items = [...this.items, ...newItems];
        this.uploadingChange.emit(true);

        forkJoin(
            newItems.map((item) => this.reportProblemService.uploadAttachment(item.file).pipe(
                map((attachment) => ({ item, attachment })),
                catchError(() => of({ item, attachment: null as ReportAttachment })),
            )),
        )
            .pipe(takeUntil(this.destroy$))
            .subscribe((results) => {
                results.forEach(({ item, attachment }) => {
                    // The reporter may have removed the tile while it was uploading.
                    if (!this.items.includes(item)) return;
                    if (attachment) {
                        item.uploading = false;
                        item.attachment = attachment;
                    } else {
                        this.discard(item);
                        this.rejections = [...this.rejections, { name: item.file.name, reason: 'upload' }];
                    }
                });
                this.emitState();
            });
    }

    remove(item: AttachmentItem): void {
        if (this.disabled) return;
        this.rejections = [];
        this.discard(item);
        this.emitState();
    }

    ngOnDestroy(): void {
        this.items.forEach((item) => item.objectUrl && URL.revokeObjectURL(item.objectUrl));
        this.destroy$.next();
        this.destroy$.complete();
    }

    private setDragOver(isDragOver: boolean): void {
        if (this.isDragOver !== isDragOver) {
            this.isDragOver = isDragOver;
            this.dragOverChange.emit(isDragOver);
        }
    }

    private isAcceptedType(file: File): boolean {
        // The name is only a fallback when the browser gives no MIME type (e.g. HEIC on some systems).
        if (file.type) {
            return file.type.startsWith('image/') || file.type === 'application/pdf';
        }
        return ACCEPTED_NAME_PATTERN.test(file.name);
    }

    private toItem(file: File): AttachmentItem {
        const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
        if (isPdf) {
            return { file, isPdf, uploading: true };
        }
        const objectUrl = URL.createObjectURL(file);
        return {
            file,
            isPdf,
            objectUrl,
            previewUrl: this.sanitizer.bypassSecurityTrustUrl(objectUrl),
            uploading: true,
        };
    }

    private discard(item: AttachmentItem): void {
        if (item.objectUrl) URL.revokeObjectURL(item.objectUrl);
        this.items = this.items.filter((other) => other !== item);
    }

    private emitState(): void {
        this.attachmentsChanged.emit(this.items.filter((item) => item.attachment).map((item) => item.attachment));
        this.uploadingChange.emit(this.uploading);
    }
}
